import React, { useState, useMemo, useCallback } from 'react';
import { Tabs as MuiTabs, Tab as MuiTab, Box } from '@mui/material';
import { TabsProps } from './Tabs.props';
import { TabPanel } from './TabPanel';

export const TabsV2 = React.memo(
  ({
    tabs,
    children,
    value: controlledValue,
    defaultValue,
    onChange,
    iconPosition = 'top',
    centered = false,
    indicatorColor = 'primary',
    textColor = 'primary',
    variant = 'standard',
    tabPanelProps,
    ...props
  }: TabsProps) => {
    // Support both controlled and uncontrolled modes
    const [internalValue, setInternalValue] = useState<string>(
      defaultValue || tabs[0]?.value || ''
    );

    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : internalValue;

    const handleChange = useCallback(
      (_event: React.SyntheticEvent, newValue: string) => {
        if (!isControlled) {
          setInternalValue(newValue);
        }
        onChange?.(newValue);
      },
      [isControlled, onChange]
    );

    // Determine if we should use standard MUI color props or custom colors via sx
    const isStandardIndicatorColor =
      indicatorColor === 'primary' || indicatorColor === 'secondary';
    const isStandardTextColor =
      textColor === 'primary' ||
      textColor === 'secondary' ||
      textColor === 'inherit';

    const customSx = useMemo(
      () => ({
        ...(!isStandardIndicatorColor && {
          '& .MuiTabs-indicator': {
            backgroundColor: indicatorColor,
          },
        }),
        ...(!isStandardTextColor && {
          '& .MuiTab-root': {
            color: textColor,
          },
          '& .Mui-selected': {
            color: textColor,
          },
        }),
        ...props.sx,
      }),
      [
        isStandardIndicatorColor,
        indicatorColor,
        isStandardTextColor,
        textColor,
        props.sx,
      ]
    );

    // Convert children array to array if it's not already
    const childrenArray = React.Children.toArray(children);

    // Ensure we have matching children for each tab
    const hasChildren = childrenArray.length > 0;

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <MuiTabs
          value={currentValue}
          onChange={handleChange}
          indicatorColor={isStandardIndicatorColor ? indicatorColor : undefined}
          textColor={isStandardTextColor ? textColor : undefined}
          variant={variant}
          centered={centered}
          scrollButtons="auto"
          allowScrollButtonsMobile
          {...props}
          sx={customSx}
        >
          {tabs.map((tab) => (
            <MuiTab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              icon={tab.icon}
              iconPosition={iconPosition}
              disabled={tab.disabled}
              id={`tab-${tab.value}`}
              aria-controls={`tabpanel-${tab.value}`}
              {...tab.tabProps}
            />
          ))}
        </MuiTabs>

        {/* Render TabPanels from children - only render active panel for performance */}
        {hasChildren &&
          tabs.map((tab, index) => (
            <TabPanel
              key={tab.value}
              value={tab.value}
              activeValue={currentValue}
              {...tabPanelProps}
            >
              {childrenArray[index]}
            </TabPanel>
          ))}
      </Box>
    );
  }
);

TabsV2.displayName = 'TabsV2';

export default TabsV2;
